import moment from 'moment-timezone';

// What the CRM sync panel shows and allows (MS-6629, T6.4), decided here so the panel is a
// rendering of these answers and the rules can be tested without a DOM.
//
// The rows are the redacted view book-api serves at GET /bookings/:id/crm/events/ (MS-6615,
// MS-6646): status, machine reason, the provider's instruction, attempts, codes and timestamps —
// never the request, the response or the provider's verbatim message, which are personal data and
// live behind the audited raw route. So "last OP message verbatim" from the ticket is,
// deliberately, the instruction plus the HTTP and error codes; nothing here reconstructs the
// message.

// Plain words for each outbox event. The payload is withheld, so "Status → Booked" cannot be
// said; "Status changed" can.
export const EVENT_LABELS = {
  created: 'New booking',
  unit_changed: 'Unit changed',
  buyer_changed: 'Main buyer changed',
  joint_buyers_changed: 'Joint buyers',
  contact_details_changed: 'Buyer details',
  spa_or_loan_updated: 'SPA / loan',
  status_changed: 'Status changed',
  cancelled: 'Cancelled',
  vp_updated: 'VP / key collection',
  reinstated: 'Reinstated',
};

export function eventLabel(event) {
  const key = String(event || '').trim();
  return EVENT_LABELS[key] || key;
}

export const PARKED = ['failed', 'needs_human'];
export const NON_TERMINAL = ['pending', 'in_flight', 'failed', 'needs_human'];

export function isParked(row) {
  return Boolean(row) && PARKED.indexOf(row.status) >= 0;
}

export function isNonTerminal(row) {
  return Boolean(row) && NON_TERMINAL.indexOf(row.status) >= 0;
}

// statusChip is the one-word state and its colour. `retrying` is the ticket's word for a pending
// row that has already been delivered at least once and is waiting out its backoff.
export function statusChip(row) {
  const r = row || {};
  switch (r.status) {
    case 'pending':
      return r.attempts > 0
        ? { key: 'retrying', label: 'Retrying', color: 'yellow' }
        : { key: 'pending', label: 'Pending', color: 'grey' };
    case 'in_flight': return { key: 'in_flight', label: 'Sending', color: 'blue' };
    case 'sent': return { key: 'sent', label: 'Sent', color: 'green' };
    case 'failed': return { key: 'failed', label: 'Failed', color: 'red' };
    case 'needs_human': return { key: 'needs_human', label: 'Needs attention', color: 'orange' };
    case 'superseded': return { key: 'superseded', label: 'Superseded', color: 'grey' };
    case 'resolved': return { key: 'resolved', label: 'Resolved', color: 'teal' };
    default: return { key: 'unknown', label: String(r.status || ''), color: 'grey' };
  }
}

// sortRows orders by provider, then by seq — the order the worker delivers in.
export function sortRows(rows) {
  return (rows || []).slice().sort((a, b) => {
    if (a.provider !== b.provider) return a.provider < b.provider ? -1 : 1;
    return (a.seq || 0) - (b.seq || 0);
  });
}

// retryableIDs is MS-6615's rule, so the button is disabled where the server would answer 409:
// only a parked row may be retried, and only the OLDEST non-terminal row of its (booking,
// provider) — retrying a later one first would deliver calls out of order.
export function retryableIDs(rows) {
  const oldest = {};
  sortRows(rows).forEach((r) => {
    if (isNonTerminal(r) && !(r.provider in oldest)) oldest[r.provider] = r;
  });
  const ids = {};
  Object.keys(oldest).forEach((p) => {
    if (isParked(oldest[p])) ids[oldest[p].id] = true;
  });
  return ids;
}

// canResolve: any parked row may be resolved (a person may decide the later row is the one that
// matters); the note is what the server requires, and the panel asks for it before posting.
export function canResolve(row) {
  return isParked(row);
}

// formatWhen renders a timestamp the way the overview's other integration blocks do, or '' when
// there is none: a missing time is a fact ("never sent"), not an error.
export function formatWhen(ts) {
  if (!ts) return '';
  const m = moment(ts);
  return m.isValid() ? m.format('D MMM YYYY, h:mm a') : '';
}

// lastAnswer is the provider's last answer as codes, or '' when it never answered. `t` formats the
// two fragments so the words are localised; the codes themselves stay as the provider gave them.
export function lastAnswer(row, t = (k, o) => k.replace(/\{\{(\w+)\}\}/g, (m, n) => o[n])) {
  const r = row || {};
  const parts = [];
  if (r.lastHTTPStatus) parts.push(t('HTTP {{status}}', { status: r.lastHTTPStatus }));
  if (r.lastErrorCode) parts.push(t('error {{code}}', { code: r.lastErrorCode }));
  return parts.join(' · ');
}

// A pending cancellation can have zero attempts because a prior event needs reconciliation.
// Keep this scoped to the provider's ordered chain, matching worker eligibility.
export function blockingRow(row, rows) {
  if (!row || row.status !== 'pending') return null;
  return sortRows(rows).find(older => older.provider === row.provider &&
    older.seq < row.seq && isNonTerminal(older)) || null;
}



// WEBPACK FOOTER //
// ./src/utils/crmSync.js